import { useCallback, useEffect, useState } from "react"
import { area, buffer, difference, feature, featureCollection, union, intersect } from "@turf/turf"
import type { Polygon, Feature, MultiPolygon } from "geojson"

import type { IParcel, IPLUData, ICommune } from "@/types/explore"
import type { MapboxGeoJSONFeature } from "mapbox-gl"
import { getPLUData } from "@/lib/explore/api/plu"

export type ActiveStudyTool = "polygon" | "forbidden" | null

export const RETRAIT_POLYGON_COLOR = "#46A097"
export const DRAWN_POLYGON_COLOR = "#D97706"

function computeRetraitFromEmprise(parcelPolygon: Feature<MultiPolygon | Polygon>, targetEmpriseRatio: number): number {
  const totalArea = area(parcelPolygon)
  const targetArea = totalArea * targetEmpriseRatio

  let lo = 0,
    hi = 50
  for (let i = 0; i < 15; i++) {
    const mid = (lo + hi) / 2
    const buffered = buffer(parcelPolygon, -mid, { units: "meters", steps: 1 })
    if (!buffered) {
      hi = mid
      continue
    }
    const bufferedArea = area(buffered)
    if (bufferedArea > targetArea) lo = mid
    else hi = mid
  }
  return Math.ceil(hi * 2) / 2
}

interface UseConstructionStudyProps {
  parcels: IParcel[]
  selectedPLU?: MapboxGeoJSONFeature | null
  selectedCommune?: ICommune
}

export function useConstructionStudy({ parcels, selectedPLU, selectedCommune }: UseConstructionStudyProps) {
  // State
  const [defaultHeight, setDefaultHeight] = useState<number>(3)
  const [edge, setEdge] = useState<number>(3)
  const [displayMode, setDisplayMode] = useState<"3D" | "2D">("3D")
  const [fetchedPLUData, setFetchedPLUData] = useState<IPLUData | null>(null)
  const [isLoadingPLUData, setIsLoadingPLUData] = useState<boolean>(false)
  const [pluDataNotFound, setPluDataNotFound] = useState<boolean>(false)

  // Polygons
  const [basePolygon, setBasePolygon] = useState<Feature<MultiPolygon | Polygon> | undefined>()
  const [finalPolygons, setFinalPolygons] = useState<Feature<MultiPolygon | Polygon>[]>([])
  const [forbiddenPolygons, setForbiddenPolygons] = useState<Feature<MultiPolygon | Polygon>[]>([])

  // Flags
  const [isCleared, setIsCleared] = useState<boolean>(false)
  const [hasRetraitPolygon, setHasRetraitPolygon] = useState<boolean>(true)
  const [isRetraitValidated, setIsRetraitValidated] = useState<boolean>(false)
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [editingPolygon, setEditingPolygon] = useState<Feature<MultiPolygon | Polygon> | undefined>()
  const [activeStudyTool, setActiveStudyTool] = useState<ActiveStudyTool>(null)

  // Update sliders when PLU data arrives
  useEffect(() => {
    if (!fetchedPLUData) return
    if (fetchedPLUData.maxHeight) setDefaultHeight(fetchedPLUData.maxHeight)
    if (fetchedPLUData.maxSurface) {
      const parcelPolygon = generateRestrictedPolygon()
      if (parcelPolygon) {
        const retrait = computeRetraitFromEmprise(parcelPolygon, fetchedPLUData.maxSurface / 100)
        setEdge(retrait)
      }
    } else {
      setEdge(0)
    }
  }, [fetchedPLUData])

  const generateRestrictedPolygon = useCallback(() => {
    if (!parcels || parcels.length === 0) return undefined
    const validParcels = parcels.filter((p) => p.geometry)
    if (validParcels.length === 0) return undefined
    const fc = featureCollection(validParcels.map((p) => feature(p.geometry)))
    const combined: Feature<Polygon | MultiPolygon> | null =
      validParcels.length > 1 ? union(fc) : feature(validParcels[0].geometry)
    return combined
  }, [parcels])

  const calculateBasePolygon = useCallback((): Feature<MultiPolygon | Polygon> | undefined => {
    if (!hasRetraitPolygon) return undefined
    const combined = generateRestrictedPolygon()
    if (!combined) return undefined
    let buffered: Feature<MultiPolygon | Polygon> | undefined | null = buffer(combined, -edge, {
      units: "meters",
      steps: 1,
    })
    if (!buffered) return

    const id = new Date().getTime()
    buffered.properties = {
      ...buffered?.properties,
      color: RETRAIT_POLYGON_COLOR,
      uniqid: id,
      height: defaultHeight,
    }
    buffered.id = id

    if (buffered && forbiddenPolygons.length > 0) {
      buffered = difference(featureCollection([buffered, ...forbiddenPolygons]))
    }

    return buffered!
  }, [edge, defaultHeight, parcels, forbiddenPolygons, hasRetraitPolygon])

  const restrictToSelectedParcels = (
    polygons: Feature<MultiPolygon | Polygon>[],
  ): Feature<MultiPolygon | Polygon>[] => {
    return polygons
      .map((poly: Feature<MultiPolygon | Polygon>) => {
        const restrictedParcel = generateRestrictedPolygon()
        if (restrictedParcel != null) {
          const intersection = intersect(featureCollection([poly, restrictedParcel]))
          if (intersection) {
            intersection.id = poly.id
            intersection.properties = { ...poly.properties }
            return intersection
          }
        }
        return undefined
      })
      .filter((poly): poly is Feature<MultiPolygon | Polygon> => poly !== undefined) as Feature<
      MultiPolygon | Polygon
    >[]
  }

  const calculatePolygons = useCallback((): Feature<MultiPolygon | Polygon>[] => {
    if (finalPolygons.length === 0) return []
    const includedParcels = restrictToSelectedParcels(finalPolygons)

    if (forbiddenPolygons.length === 0 && finalPolygons.length === 1) return includedParcels
    let diff: Feature<MultiPolygon | Polygon>[] = []

    if (forbiddenPolygons.length !== 0) {
      diff = includedParcels
        .map((poly) => {
          const d: Feature<MultiPolygon | Polygon> | null = difference(featureCollection([poly, ...forbiddenPolygons]))
          if (d) return { ...d, id: poly.id, type: "Feature" as const }
        })
        .filter((poly) => poly !== undefined) as Feature<MultiPolygon | Polygon>[]
    }

    if (!diff || diff?.length === 0) {
      diff = includedParcels.map((poly) => {
        return {
          ...poly,
          id: poly.id ?? poly?.properties?.uniqid,
          type: "Feature" as const,
        }
      })
    }

    if (diff && diff?.length > 1) {
      const diffBtwPolygons = difference(featureCollection([diff[diff.length - 1], ...diff.slice(0, diff.length - 1)]))

      if (diffBtwPolygons) {
        diff[diff.length - 1] = {
          ...diffBtwPolygons,
          id: diffBtwPolygons.properties?.uniqid,
        }
        return diff
      } else {
        return diff.slice(0, diff.length - 1)
      }
    }
    return diff
  }, [finalPolygons, forbiddenPolygons])

  const onUpdate = (updateEvent: Record<string, unknown>) => {
    const updatedPolygon = (updateEvent.features as Feature[] | undefined)?.[0]
    if (!updatedPolygon) return
    const index = finalPolygons.findIndex((poly) => poly.id === updatedPolygon.id)
    if (index === -1) return
    const restrictedUpdatedPolygon = restrictToSelectedParcels([updatedPolygon as Feature<MultiPolygon | Polygon>])
    let polygonsToDifference

    if (index === 0) {
      polygonsToDifference = finalPolygons.slice(1)
    } else {
      polygonsToDifference = finalPolygons.slice(0, index)
    }
    const diffBtwPolygons =
      finalPolygons.length > 1
        ? difference(featureCollection([restrictedUpdatedPolygon[0], ...polygonsToDifference]))
        : null

    let updated
    if (diffBtwPolygons) {
      updated = [
        ...finalPolygons.slice(0, index),
        { ...diffBtwPolygons, id: finalPolygons[index].id },
        ...finalPolygons.slice(index + 1),
      ]
    } else {
      updated = [...finalPolygons.slice(0, index), restrictedUpdatedPolygon[0], ...finalPolygons.slice(index + 1)]
    }

    if (forbiddenPolygons.length !== 0) {
      updated = updated
        .map((poly) => {
          const d: Feature<MultiPolygon | Polygon> | null = difference(featureCollection([poly, ...forbiddenPolygons]))
          if (d) return { ...d, id: poly.id, type: "Feature" as const }
        })
        .filter((poly) => poly !== undefined) as Feature<MultiPolygon | Polygon>[]
    }

    setFinalPolygons(updated)
  }

  const createRetraitPolygon = useCallback(() => {
    setIsCleared(false)
    setHasRetraitPolygon(true)
  }, [])

  const removeRetraitPolygon = useCallback(() => {
    setHasRetraitPolygon(false)
    setIsRetraitValidated(false)
    setBasePolygon(undefined)
  }, [])

  useEffect(() => {
    const newPolygons = calculatePolygons()
    setFinalPolygons((prev) => {
      if (prev.length === newPolygons.length && prev.every((p, i) => p === newPolygons[i])) return prev
      return newPolygons
    })
  }, [calculatePolygons])

  useEffect(() => {
    if (isCleared) return
    if (!hasRetraitPolygon) {
      setBasePolygon(undefined)
      return
    }
    setBasePolygon(calculateBasePolygon())
  }, [calculateBasePolygon, isCleared, hasRetraitPolygon])

  // PLU data fetching
  useEffect(() => {
    const cityName = selectedCommune?.libelle || selectedCommune?.nom
    const zipCode = selectedCommune?.codesPostaux?.[0]
    const parcelCenter = parcels?.[0]?.center
    const libelle = selectedPLU?.properties?.libelle as string | undefined
    if (!cityName || !zipCode) return
    if (!libelle && !parcelCenter) return

    const abortController = new AbortController()
    setIsLoadingPLUData(true)
    getPLUData(zipCode, cityName, libelle, parcelCenter?.[0], parcelCenter?.[1])
      .then((data) => {
        if (abortController.signal.aborted) return
        if (data) {
          setFetchedPLUData(data)
          setPluDataNotFound(false)
        } else {
          setPluDataNotFound(true)
        }
      })
      .catch(() => {
        if (!abortController.signal.aborted) {
          setPluDataNotFound(true)
        }
      })
      .finally(() => {
        if (!abortController.signal.aborted) {
          setIsLoadingPLUData(false)
        }
      })

    return () => {
      abortController.abort()
    }
  }, [selectedPLU, selectedCommune, parcels])

  const onPolygonCreated = useCallback(
    (polygon: Feature<Polygon>) => {
      const id = new Date().getTime()
      polygon.properties = {
        ...polygon.properties,
        uniqid: id,
        height: defaultHeight,
        color: DRAWN_POLYGON_COLOR,
      }
      polygon.id = id
      setFinalPolygons((prev) => [...prev, polygon])
    },
    [defaultHeight],
  )

  const onForbiddenPolygonCreated = useCallback((polygon: Feature<Polygon>) => {
    polygon.properties = {
      ...polygon.properties,
    }
    setForbiddenPolygons((prev) => [...prev, polygon])
  }, [])

  const resetStudy = useCallback(() => {
    setFinalPolygons([])
    setForbiddenPolygons([])
    setBasePolygon(undefined)
    setHasRetraitPolygon(false)
    setIsRetraitValidated(false)
    setIsEditing(false)
    setEditingPolygon(undefined)
    setActiveStudyTool(null)
    setIsCleared(true)
  }, [])

  return {
    // State
    defaultHeight,
    setDefaultHeight,
    edge,
    setEdge,
    displayMode,
    setDisplayMode,
    hasRetraitPolygon,
    isRetraitValidated,
    setIsRetraitValidated,
    basePolygon,
    finalPolygons,
    setFinalPolygons,
    forbiddenPolygons,
    fetchedPLUData,
    isLoadingPLUData,
    pluDataNotFound,
    isEditing,
    setIsEditing,
    editingPolygon,
    setEditingPolygon,
    activeStudyTool,
    setActiveStudyTool,
    // Callbacks
    generateRestrictedPolygon,
    createRetraitPolygon,
    removeRetraitPolygon,
    onUpdate,
    onPolygonCreated,
    onForbiddenPolygonCreated,
    resetStudy,
    isCleared,
    setIsCleared,
    parcelSurface: parcels?.[0]?.properties?.contenance,
  }
}

export type UseConstructionStudyReturn = ReturnType<typeof useConstructionStudy>
